/**
 * 월간 스케줄 달력 (공용).
 *
 * 원장이 스케줄 달력을 보는 곳이 두 군데인데(월간 계획 탭 / 회원 상세) 각자 따로
 * 그리다가 한쪽에만 사유서가 보이는 상태가 됐다. 달력 칸을 그리는 곳은 여기 하나다.
 */
import React, { useMemo } from 'react';

export interface CalendarEntry {
  schedule_date: string;
  period_numbers?: number[] | null;
  check_in_time?: string | null;
  check_out_time?: string | null;
  is_absent?: boolean;
  absence_reason?: string | null;
  note?: string | null;
}

export interface CalendarException {
  exception_date: string;
  exception_type: string;
  reason?: string | null;
  status: string;
}

interface Props {
  year: number;
  /** 1~12 */
  month: number;
  entries: CalendarEntry[];
  exceptions?: CalendarException[];
  showLegend?: boolean;
}

const WEEKDAYS = ['월', '화', '수', '목', '금', '토', '일'];

const hhmm = (v?: string | null) => (v ? v.slice(0, 5) : null);

const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const excStyle = (status: string) =>
  status === 'approved'
    ? 'bg-green-100 text-green-700'
    : status === 'rejected'
    ? 'bg-red-100 text-red-700'
    : 'bg-yellow-100 text-yellow-800';

const excMark = (status: string) => (status === 'approved' ? '✓' : status === 'rejected' ? '✗' : '⏱');

const MonthlyScheduleCalendar: React.FC<Props> = ({
  year,
  month,
  entries,
  exceptions = [],
  showLegend = true,
}) => {
  // 월요일 시작 6주 고정 그리드
  const cells = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const start = new Date(year, month - 1, 1 - startOffset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return { date: d, inMonth: d.getMonth() === month - 1 };
    });
  }, [year, month]);

  const entryByDate = useMemo(() => {
    const m: Record<string, CalendarEntry> = {};
    entries.forEach((e) => {
      m[e.schedule_date] = e;
    });
    return m;
  }, [entries]);

  const excByDate = useMemo(() => {
    const m: Record<string, CalendarException> = {};
    exceptions.forEach((e) => {
      m[e.exception_date] = e;
    });
    return m;
  }, [exceptions]);

  return (
    <div className="space-y-3">
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {WEEKDAYS.map((d, i) => (
            <div
              key={d}
              className={`px-2 py-2 text-center text-xs font-semibold ${
                i === 5 ? 'text-blue-600' : i === 6 ? 'text-red-600' : 'text-gray-700'
              }`}
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map(({ date, inMonth }, idx) => {
            const key = fmtDate(date);
            const entry = inMonth ? entryByDate[key] : undefined;
            const exc = inMonth ? excByDate[key] : undefined;
            const dow = (date.getDay() + 6) % 7;
            const dowColor = dow === 5 ? 'text-blue-600' : dow === 6 ? 'text-red-600' : 'text-gray-700';
            const inTime = hhmm(entry?.check_in_time);
            const outTime = hhmm(entry?.check_out_time);
            const periodCount = entry?.period_numbers?.length || 0;

            return (
              <div
                key={idx}
                className={`border-b border-r border-gray-100 p-2 min-h-[80px] ${
                  inMonth ? 'bg-white' : 'bg-gray-50/50'
                }`}
              >
                <div className={`text-xs font-medium ${inMonth ? dowColor : 'text-gray-300'}`}>
                  {date.getDate()}
                </div>

                {entry?.is_absent && (
                  <div
                    className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600"
                    title={entry.absence_reason || ''}
                  >
                    결석
                  </div>
                )}

                {entry && !entry.is_absent && inTime && (
                  <div className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">
                    {outTime ? `${inTime}~${outTime}` : inTime}
                  </div>
                )}

                {entry && !entry.is_absent && !inTime && periodCount > 0 && (
                  <div className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">
                    {periodCount}교시
                  </div>
                )}

                {exc && (
                  <div
                    className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${excStyle(
                      exc.status
                    )}`}
                    title={exc.reason || ''}
                  >
                    {exc.exception_type}
                    {excMark(exc.status)}
                  </div>
                )}

                {entry?.note && (
                  <div className="mt-1 text-[10px] text-amber-600 truncate" title={entry.note}>
                    {entry.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showLegend && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-blue-100 border border-blue-200"></span>
            등원 예정
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-green-100 border border-green-200"></span>
            사유서 승인
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-200"></span>
            사유서 대기
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-red-100 border border-red-200"></span>
            사유서 거절
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-gray-100 border border-gray-200"></span>
            결석
          </span>
        </div>
      )}
    </div>
  );
};

export default MonthlyScheduleCalendar;
